/**
 * Professional first-message templates for contractors writing to clients.
 *
 * Prod transcripts showed openers of "Hi" / "Hi" / "Hi", bare phone numbers,
 * and "Goodday" with no ask. Every template here introduces the contractor
 * by name and business, states their experience, says what they need to
 * price the work, and proposes one next step. Everything is filled from the
 * contractor's own profile so the text reads as theirs, and it lands in the
 * composer as editable text — never auto-sent.
 */

const clean = (s) => String(s ?? '').trim();

/** Normalise the two user shapes the app passes around (/auth/me vs legacy). */
export function contractorFacts(user) {
  const p = user?.profile || {};
  const firstName = clean(p.firstName || p.first_name || user?.first_name || user?.firstName);
  const lastName = clean(p.lastName || p.last_name || user?.last_name || user?.lastName);
  const company = clean(p.companyName || p.company_name || user?.company_name || user?.companyName);
  const years = Number(p.yearsExperience ?? p.years_experience ?? user?.years_experience ?? 0) || 0;
  const trade = clean(p.primaryTrade || p.primary_trade || user?.primary_trade).toLowerCase();
  const verified = Boolean(p.businessVerified ?? p.business_verified ?? user?.business_verified);
  const rating = Number(p.averageRating ?? p.average_rating ?? 0) || 0;
  const reviews = Number(p.totalReviews ?? p.total_reviews ?? 0) || 0;
  return { firstName, lastName, company, years, trade, verified, rating, reviews };
}

/** What the profile is missing for a complete introduction (for the hint line). */
export function missingIntroFacts(user) {
  const f = contractorFacts(user);
  const missing = [];
  if (!f.firstName) missing.push('your name');
  if (!f.company) missing.push('your business name');
  if (!f.years) missing.push('years of experience');
  if (!f.trade) missing.push('your main trade');
  return missing;
}

/** "I am Andrew Lijuni from Frontnote Construction, a building contractor with 12 years' experience, verified on Eqaya." */
export function introSentence(user) {
  const f = contractorFacts(user);
  const name = [f.firstName, f.lastName].filter(Boolean).join(' ');
  if (!name && !f.company) return '';
  const who = name ? `I am ${name}${f.company ? ` from ${f.company}` : ''}` : `We are ${f.company}`;
  const descriptors = [];
  if (f.trade) descriptors.push(`${/^[aeiou]/.test(f.trade) ? 'an' : 'a'} ${f.trade} contractor`);
  if (f.years) descriptors.push(`${f.years} year${f.years === 1 ? '' : 's'}' experience`);
  let tail = '';
  if (descriptors.length === 2) tail = `, ${descriptors[0]} with ${descriptors[1]}`;
  else if (f.trade) tail = `, ${descriptors[0]}`;
  else if (f.years) tail = `, with ${descriptors[0]}`;
  const flags = [];
  if (f.verified) flags.push('verified on Eqaya');
  if (f.reviews >= 3 && f.rating >= 4) flags.push(`rated ${f.rating.toFixed(1)} from ${f.reviews} reviews`);
  const flagText = flags.length ? `, ${flags.join(' and ')}` : '';
  return `${who}${tail}${flagText}.`;
}

function signOff(user) {
  const f = contractorFacts(user);
  const name = [f.firstName, f.lastName].filter(Boolean).join(' ');
  return ['Regards,', [name, f.company].filter(Boolean).join(', ')].filter(Boolean).join('\n');
}

function greeting(clientName) {
  const n = clean(clientName).split(/\s+/)[0];
  return n ? `Good day ${n},` : 'Good day,';
}

function projectRef(projectTitle) {
  const t = clean(projectTitle);
  return t ? `your project "${t}"` : 'your project';
}

/**
 * The templates. Each returns full message text.
 * `needs` (optional) is a list of things the contractor wants before pricing,
 * e.g. ['a site visit', 'the building plans'].
 */
export const INTRO_TEMPLATES = [
  {
    key: 'introduce',
    label: 'Introduce yourself & ask for details',
    build: ({ user, clientName, projectTitle, needs }) => {
      const list = (needs && needs.length) ? needs : ['a site visit or clear photos of the site', 'any plans, drawings or measurements you have'];
      return [
        greeting(clientName),
        '',
        [`Thank you for listing ${projectRef(projectTitle)} on Eqaya.`, introSentence(user)].filter(Boolean).join(' '),
        '',
        'To give you an accurate price rather than a guess, I need:',
        ...list.map((n) => `- ${n}`),
        '',
        'Could you share what you already have, or suggest a time for a site visit or a call? I will then send you an official quotation through Eqaya.',
        '',
        signOff(user),
      ].join('\n');
    },
  },
  {
    key: 'site_visit',
    label: 'Propose a site visit',
    build: ({ user, clientName, projectTitle }) => [
      greeting(clientName),
      '',
      [`I am interested in ${projectRef(projectTitle)}.`, introSentence(user)].filter(Boolean).join(' '),
      '',
      'The best way for me to price this properly is to see the site. Could you propose two or three times this week that suit you, or whoever will be at the property? I will confirm one and come prepared to take measurements and photos.',
      '',
      'After the visit I will send you an official quotation through Eqaya, broken into stages you can fund one at a time.',
      '',
      signOff(user),
    ].join('\n'),
  },
  {
    key: 'ready',
    label: 'Say a quotation is coming',
    build: ({ user, clientName, projectTitle }) => [
      greeting(clientName),
      '',
      [`Thank you for the details on ${projectRef(projectTitle)}.`, introSentence(user)].filter(Boolean).join(' '),
      '',
      'I now have what I need. I am preparing your official quotation on Eqaya and will send it within two working days. It will show the scope, materials, timeline and what is excluded, in stages so you only fund one stage at a time.',
      '',
      'If anything changes on your side before then, please let me know here.',
      '',
      signOff(user),
    ].join('\n'),
  },
];

/**
 * True when a first message is too thin to get a reply: a bare greeting, a
 * lone phone number, or under four words with no ask.
 */
export function isThinOpener(text) {
  const t = clean(text);
  if (!t) return true;
  const digits = t.replace(/\D/g, '');
  if (digits.length >= 9 && t.replace(/[\d\s+()-]/g, '').length <= 3) return true; // phone number only
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= 3) return true;
  return /^(hi|hie|hello|hey|good\s*(day|morning|afternoon|evening)|greetings|goodday)[\s!.,]*$/i.test(t);
}
